/**
 * Role Entity
 * Represents a user role with permissions
 */
export class Role {
    private constructor(
        private readonly _roleId: string,
        private readonly _name: string,
        private readonly _description: string,
        private readonly _isActive: boolean,
        private readonly _permissions: string[],
        private readonly _createdAt: Date,
        private readonly _updatedAt: Date
    ) { }

    static create(props: {
        roleId: string;
        name: string;
        description: string;
        isActive: boolean;
        permissions: string[];
        createdAt: Date;
        updatedAt: Date;
    }): Role {
        return new Role(
            props.roleId,
            props.name,
            props.description,
            props.isActive,
            props.permissions,
            props.createdAt,
            props.updatedAt
        );
    }

    get roleId(): string {
        return this._roleId;
    }

    get name(): string {
        return this._name;
    }

    /**
     * Get formatted display name
     * Converts snake_case or underscore-separated names to Title Case
     * Examples: "super_admin" -> "Super Admin", "content_creator" -> "Content Creator"
     */
    get displayName(): string {
        return this._name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    get description(): string {
        return this._description;
    }

    get isActive(): boolean {
        return this._isActive;
    }

    get permissions(): string[] {
        return [...this._permissions];
    }

    get createdAt(): Date {
        return this._createdAt;
    }

    get updatedAt(): Date {
        return this._updatedAt;
    }

    /**
     * Check if role has a specific permission
     */
    hasPermission(permission: string): boolean {
        return this._permissions.includes('*') || this._permissions.includes(permission);
    }

    /**
     * Check if role has all specified permissions
     */
    hasAllPermissions(permissions: string[]): boolean {
        if (this._permissions.includes('*')) {
            return true;
        }
        return permissions.every(permission => this._permissions.includes(permission));
    }

    /**
     * Check if role has any of the specified permissions
     */
    hasAnyPermission(permissions: string[]): boolean {
        if (this._permissions.includes('*')) {
            return true;
        }
        return permissions.some(permission => this._permissions.includes(permission));
    }
}
